import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import f1Service from '../../../components/dashboards/services/f1Service';

/**
 * F1 Season Calendar & Race Tracker
 * Interactive race timeline with circuit info and results
 * Shows upcoming, live, and completed races with status indicators
 */
const F1SeasonCalendar = ({ season = 2025 }) => {
  const [races, setRaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRace, setExpandedRace] = useState(null);
  const [raceWinners, setRaceWinners] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const RACES_PER_PAGE = 6;
 
  // Helper function to get race status (used in sorting)
  const getRaceStatus = (race) => {
    // Trust API status first
    if (race.status === 'Completed') return 'completed';
    if (race.status === 'Cancelled') return 'cancelled';
    if (race.status === 'Live') return 'live';

    // Fallback to date-based detection
    const now = new Date();
    const raceDate = new Date(race.date);

    // Check if race is today (could be live)
    const today = now.toISOString().split('T')[0];
    const raceDateStr = race.date.split('T')[0];
    if (raceDateStr === today) {
      return 'live';
    }

    // Check if race is in the past
    if (raceDate < now) {
      return 'completed';
    }

    // Future race
    return 'upcoming';
  };

  useEffect(() => {
    const fetchRaces = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await f1Service.getCurrentSeasonRaces();

        if (response && response.response) {
          // Status priority: Live (1) > Upcoming (2) > Completed (3) > Cancelled (4)
          const statusPriority = {
            'live': 1,
            'upcoming': 2,
            'completed': 3,
            'cancelled': 4
          };

          // Sort races by status priority first, then by date
          const sortedRaces = response.response.sort((a, b) => {
            const statusA = getRaceStatus(a);
            const statusB = getRaceStatus(b);
            const priorityA = statusPriority[statusA] || 5;
            const priorityB = statusPriority[statusB] || 5;

            // If different status, sort by priority
            if (priorityA !== priorityB) {
              return priorityA - priorityB;
            }

            // Same status, sort by date
            // For upcoming/live: earliest first
            // For completed: most recent first
            if (statusA === 'upcoming' || statusA === 'live') {
              return new Date(a.date) - new Date(b.date);
            } else {
              return new Date(b.date) - new Date(a.date);
            }
          });
          setRaces(sortedRaces);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching races:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchRaces();
  }, [season]);

  // Calculate pagination
  const totalPages = Math.ceil(races.length / RACES_PER_PAGE);
  const startIndex = (currentPage - 1) * RACES_PER_PAGE;
  const endIndex = startIndex + RACES_PER_PAGE;
  const currentRaces = races.slice(startIndex, endIndex);

  // Fetch winners for completed races on current page only
  useEffect(() => {
    const fetchWinners = async () => {
      const completedRaces = currentRaces.filter(r => {
        const status = getRaceStatus(r);
        return status === 'completed' && r.type === 'Race';
      });

      const winnersMap = { ...raceWinners }; // Keep existing winners

      for (const race of completedRaces) {
        // Skip if already fetched
        if (winnersMap[race.id]) continue;

        try {
          const results = await f1Service.getRaceResults(race.id);
          if (results?.response?.[0]) {
            winnersMap[race.id] = results.response[0]; // First position = winner
          }
        } catch (err) {
          console.log(`No results for race ${race.id}`);
        }
      }

      setRaceWinners(winnersMap);
    };

    if (currentRaces.length > 0) {
      fetchWinners();
    }
  }, [currentPage, races]);

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return '#10B981'; // Green
      case 'live':
        return '#E10600'; // F1 Red
      case 'upcoming':
        return '#F59E0B'; // Amber
      case 'cancelled':
        return '#6B7280'; // Gray
      default:
        return '#6B7280';
    }
  };

  // Get status label
  const getStatusLabel = (status) => {
    return status.toUpperCase();
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  // Format full date for expanded view
  const formatFullDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
        <div className="p-4 border-b border-white/5">
          <div className="h-4 w-32 bg-white/10 rounded animate-pulse" />
        </div>
        <div className="p-4 overflow-x-auto">
          <div className="flex gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="flex-shrink-0 w-48 h-40 bg-white/10 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white/[0.02] border border-red-500/20 rounded-sm p-4">
        <p className="text-xs text-red-400 font-mono">Error: {error}</p>
      </div>
    );
  }

  // Empty state
  if (races.length === 0) {
    return (
      <div className="bg-white/[0.02] border border-white/5 rounded-sm p-8 text-center">
        <p className="text-xs text-gray-600 font-mono uppercase tracking-wider">
          No Race Data Available
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-white/5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider font-mono">
            {season} Season Calendar
          </h3>
          <div className="flex items-center gap-4 text-[10px] font-mono uppercase tracking-wider">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getStatusColor('completed') }} />
              <span className="text-gray-600">Completed</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getStatusColor('live') }} />
              <span className="text-gray-600">Live</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getStatusColor('upcoming') }} />
              <span className="text-gray-600">Upcoming</span>
            </div>
          </div>
        </div>

        {/* Pagination Controls */}
        <div className="flex items-center justify-between">
          <div className="text-[10px] text-gray-600 font-mono">
            Showing {startIndex + 1}-{Math.min(endIndex, races.length)} of {races.length} races
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-[10px] font-mono uppercase tracking-wider bg-transparent text-gray-400 border border-white/10 hover:border-white/20 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              Prev
            </button>
            <div className="text-[10px] text-gray-400 font-mono">
              Page {currentPage} / {totalPages}
            </div>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-[10px] font-mono uppercase tracking-wider bg-transparent text-gray-400 border border-white/10 hover:border-white/20 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Race Timeline - Grid Layout */}
      <div className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {currentRaces.map((race, index) => {
            const status = getRaceStatus(race);
            const statusColor = getStatusColor(status);
            const isExpanded = expandedRace === race.id;

            return (
              <motion.div
                key={race.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05, duration: 0.2 }}
                className="cursor-pointer"
                onClick={() => setExpandedRace(isExpanded ? null : race.id)}
              >
                {/* Race Card */}
                <div
                  className="border rounded-sm transition-all duration-200 hover:border-white/20"
                  style={{
                    backgroundColor: `${statusColor}08`,
                    borderColor: isExpanded ? statusColor : '#ffffff0d'
                  }}
                >
                  {/* Circuit Image (if available) */}
                  {race.circuit?.image && (
                    <div className="h-24 overflow-hidden border-b border-white/5">
                      <img
                        src={race.circuit.image}
                        alt={race.circuit.name}
                        className="w-full h-full object-cover opacity-30"
                      />
                    </div>
                  )}

                  {/* Race Info */}
                  <div className="p-3 space-y-2">
                    {/* Round & Status */}
                    <div className="flex items-start justify-between">
                      <div className="text-[10px] font-mono text-gray-600 uppercase tracking-wider">
                        Round {race.competition?.location?.round || index + 1}
                      </div>
                      <div
                        className="text-[9px] font-bold font-mono uppercase tracking-wider px-1.5 py-0.5 rounded"
                        style={{
                          backgroundColor: `${statusColor}20`,
                          color: statusColor
                        }}
                      >
                        {getStatusLabel(status)}
                      </div>
                    </div>

                    {/* Race Name */}
                    <div className="space-y-1">
                      <div className="text-sm font-bold text-white leading-tight">
                        {race.competition?.name || 'Grand Prix'}
                      </div>
                      <div className="text-xs text-gray-500 font-mono">
                        {race.circuit?.name || 'Circuit'}
                      </div>
                    </div>

                    {/* Date */}
                    <div className="pt-2 border-t border-white/5">
                      <div className="text-xs text-gray-400 font-mono">
                        {formatDate(race.date)}
                      </div>
                    </div>

                    {/* Sprint Indicator */}
                    {race.type === 'Sprint Race' && (
                      <div className="flex items-center gap-1.5 text-[10px] text-amber-500 font-mono uppercase tracking-wider">
                        <div className="w-1 h-1 bg-amber-500 rounded-full" />
                        Sprint
                      </div>
                    )}

                    {/* Winner (if completed) */}
                    {status === 'completed' && raceWinners[race.id] && (
                      <div className="pt-2 border-t border-white/5">
                        <div className="text-[10px] text-gray-600 font-mono uppercase tracking-wider mb-1">
                          Winner
                        </div>
                        <div className="text-xs text-white font-semibold">
                          {raceWinners[race.id].driver?.name || 'TBD'}
                        </div>
                        {raceWinners[race.id].team?.name && (
                          <div className="text-[10px] text-gray-500 font-mono mt-0.5">
                            {raceWinners[race.id].team.name}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="border-t border-white/5 p-3 space-y-2 text-xs font-mono"
                    >
                      <div className="text-gray-600 uppercase tracking-wider text-[10px]">
                        Race Details
                      </div>

                      <div className="space-y-1.5">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Date</span>
                          <span className="text-white">{formatFullDate(race.date)}</span>
                        </div>

                        {race.competition?.location?.country && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Country</span>
                            <span className="text-white">{race.competition.location.country}</span>
                          </div>
                        )}

                        {race.circuit && (
                          <>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Circuit</span>
                              <span className="text-white truncate ml-2">{race.circuit.name}</span>
                            </div>

                            {race.laps && (
                              <div className="flex justify-between">
                                <span className="text-gray-500">Laps</span>
                                <span className="text-white">{race.laps.total || 'TBD'}</span>
                              </div>
                            )}

                            {race.distance && (
                              <div className="flex justify-between">
                                <span className="text-gray-500">Distance</span>
                                <span className="text-white">{race.distance}</span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-white/5 bg-white/[0.01]">
        <div className="flex items-center justify-between text-[10px] text-gray-600 font-mono uppercase tracking-wider">
          <span>Total Races</span>
          <span className="text-white font-bold">{races.length}</span>
        </div>
      </div>
    </div>
  );
};

export default F1SeasonCalendar;
